import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-level safety net. Without this, an unexpected error thrown during render unmounts
 * the whole tree and leaves the user staring at a blank page. Here we catch it, log it
 * for debugging, and show a recoverable fallback instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6">
        <div className="w-full max-w-md rounded-lg border border-ink-200 bg-white px-8 py-12 text-center shadow-glow">
          <p className="text-sm font-medium uppercase tracking-wide text-brand-500">
            Something went wrong
          </p>
          <h1 className="mt-2 text-lg font-semibold text-ink-900">This page hit an unexpected error</h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-ink-500">
            The app caught a crash before it could blank the screen. Reloading usually clears it. If
            it keeps happening, let us know what you were doing.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 inline-flex items-center justify-center rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
          >
            Reload the app
          </button>
        </div>
      </div>
    );
  }
}
